import { APIRoutes } from "@/api";
import { queryClient } from "@/lib/query-client";
import { ApiService } from "@/services/api/api-service";
import { PortfolioSchema } from "@/services/portfolios/schema";
import {
  QueryFunctionContext,
  UseQueryOptions,
  useQuery as useQueryTanstack,
} from "@tanstack/react-query";

async function getPortfolioDraft({ queryKey, signal }: QueryFunctionContext) {
  const id = queryKey[1] as string;
  const tournamentId = queryKey[2] as string;
  let url: string = APIRoutes.PORTFOLIO_DRAFT;

  if (id) {
    const params = new URLSearchParams({ id, tournamentId });
    url = `${url}?${params.toString()}`;
  }

  const data = await fetch(url, {
    cache: "no-cache",
    signal,
  }).then((res) => res.json());

  const result = ApiService.parseApiResponse(data, PortfolioSchema);

  return result.status === "success" ? result.data : null;
}

type TData = Awaited<ReturnType<typeof getPortfolioDraft>>;

export namespace PortfolioDraftQuery {
  const keys = {
    all: ["portfolio-draft"],
    userDraft: (id?: string, tournamentId?: string) => {
      if (!id) {
        return keys.all;
      } else {
        return [...keys.all, id, tournamentId];
      }
    },
  } as const;

  export function remove(id?: string) {
    return queryClient.removeQueries({
      queryKey: id ? keys.userDraft(id) : keys.all,
    });
  }

  export function invalidate(id?: string, tournamentId?: string) {
    if (id) {
      return queryClient.invalidateQueries({
        queryKey: keys.userDraft(id, tournamentId),
      });
    }

    return queryClient.invalidateQueries({
      queryKey: keys.all,
    });
  }

  export function getData(id?: string, tournamentId?: string) {
    if (!id) {
      return queryClient.getQueryData<TData>(keys.all);
    }
    return queryClient.getQueryData<TData>(keys.userDraft(id, tournamentId));
  }

  export function setData(
    id: string | undefined,
    tournamentId: string | undefined,
    data: TData,
  ) {
    if (!id) {
      return queryClient.setQueryData(keys.all, data);
    }

    return queryClient.setQueryData(keys.userDraft(id, tournamentId), data);
  }

  export function prefetch(id?: string, tournamentId?: string) {
    return queryClient.prefetchQuery({
      queryKey: keys.userDraft(id, tournamentId),
      queryFn: getPortfolioDraft,
    });
  }

  export function useQuery(
    id?: string,
    tournamentId?: string,
    options?: Omit<
      UseQueryOptions<TData>,
      "queryKey" | "queryFn" | "initialData"
    >,
  ) {
    return useQueryTanstack({
      queryKey: keys.userDraft(id, tournamentId),
      queryFn: getPortfolioDraft,
      // enabled: DemoMode.enabled || !!session?.user?.id,
      ...options,
    });
  }
}
